/**
 * Single source of truth for the currently-signed-in user's identity:
 * name + email + avatar URL. Backed by /api/auth/me on the BFF and the
 * existing /api/profile/avatar/view-url that issues a SAS to the blob.
 *
 * SSR correctness — same notes as useDisplayAccess. The previous
 * version kept `_user` at module scope and fired a non-awaited load()
 * which caused:
 *   1. Hydration mismatches — header rendered with no name/avatar
 *      during SSR (state was null), then snapped in after the client
 *      fetch resolved.
 *   2. Cross-request data leak on Node SSR — first request to populate
 *      `_user` would be visible to every concurrent SSR render until
 *      that user's request finished.
 *
 * useAsyncData fixes both: the fetch is awaited during SSR, the
 * result is serialised into the per-request Nuxt payload, and the
 * client rehydrates from the payload without re-fetching.
 *
 * The cache-key 'me:current-user' is what uploadAvatar uses to
 * invalidate via refreshNuxtData() after a successful upload, so the
 * header picks up the new avatar URL on the next reactivity tick.
 */

type CurrentUser = {
  authenticated: boolean
  name: string
  email: string | null
  oid: string | null
  roles: string[]
  avatarUrl: string | null
}

const USER_DATA_KEY = 'me:current-user'

async function fetchCurrentUser(): Promise<CurrentUser> {
  // Forward the session cookie during SSR — see comments in
  // useDisplayAccess.ts for the full explanation. Without this, the
  // SSR-side fetch reaches the BFF unauthenticated, comes back with
  // `authenticated: false`, and the header pill renders as a logged-
  // out shell even though the user is signed in.
  const headers = import.meta.server ? useRequestHeaders(['cookie']) : undefined
  const me: any = await $fetch('/api/auth/me', { credentials: 'include', headers })
  if (!me?.authenticated) {
    return {
      authenticated: false,
      name: '',
      email: null,
      oid: null,
      roles: [],
      avatarUrl: null
    }
  }
  const claims = me?.claims || {}
  const name = claims?.name || claims?.preferred_username || claims?.email || 'User'
  const email = claims?.email || claims?.preferred_username || null
  const oid = claims?.oid || claims?.sub || null

  // Cache-buster on the avatar URL so a freshly-uploaded avatar shows
  // immediately without a hard reload. Date.now() here only runs during
  // the fetch (which is SSR-awaited), so it doesn't cause a hydration
  // mismatch — the same value gets serialized into the payload and
  // rehydrated on the client.
  const avatarUrl = `/api/profile/avatar/view-url?t=${Date.now()}`

  return {
    authenticated: true,
    name,
    email,
    oid,
    roles: me?.roles ?? [],
    avatarUrl
  }
}

export function useCurrentUser() {
  const { data: user, pending, refresh } = useAsyncData<CurrentUser>(
    USER_DATA_KEY,
    fetchCurrentUser
  )
  return {
    user,
    loading: pending,
    refresh
  }
}

/**
 * Upload a new avatar. Flow:
 *   1. POST /api/profile/avatar/upload-url to get a SAS upload URL
 *   2. PUT the file bytes directly to blob storage
 *   3. Refresh the user so the avatar component picks up the new image
 */
export async function uploadAvatar(file: File): Promise<{ ok: boolean; error?: string }> {
  try {
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      return { ok: false, error: 'Only JPEG, PNG, or WebP images are allowed.' }
    }
    if (file.size > 5 * 1024 * 1024) {
      return { ok: false, error: 'Image must be smaller than 5 MB.' }
    }

    const csrfCookie = typeof document !== 'undefined'
      ? document.cookie.split('; ').find(c => c.startsWith('csrf='))?.split('=')[1] ?? ''
      : ''

    // Three-step upload:
    //   1. /upload-url → returns SAS for a TEMPORARY pending blob
    //   2. PUT the bytes to that SAS
    //   3. /commit → backend scans the image with Azure AI Content Safety
    //                (no nudity / hate / violence / self-harm), then promotes
    //                the pending blob to the user's canonical avatar path
    //                and saves it on their record. Failed moderation deletes
    //                the pending blob and surfaces the rejection reason.
    const { uploadUrl, pendingBlobName } = await $fetch<{
      uploadUrl: string
      pendingBlobName: string
    }>('/api/profile/avatar/upload-url', {
      method: 'POST',
      credentials: 'include',
      headers: { 'x-csrf': decodeURIComponent(csrfCookie) },
      body: { fileName: file.name, contentType: file.type }
    })

    const putResp = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': file.type
      },
      body: file
    })
    if (!putResp.ok) {
      return { ok: false, error: `Upload failed: ${putResp.status} ${putResp.statusText}` }
    }

    // Commit + content moderation.
    try {
      await $fetch('/api/profile/avatar/commit', {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-csrf': decodeURIComponent(csrfCookie) },
        body: { pendingBlobName }
      })
    } catch (e: any) {
      // The backend already cleaned up the pending blob on rejection;
      // surface the moderation reason to the user so they understand why.
      const reason = e?.data?.reason || e?.data?.error || e?.message || 'Image rejected.'
      return { ok: false, error: reason }
    }

    // Invalidate the useAsyncData cache for the current-user key so the
    // header pill, settings page, and anywhere else reading from
    // useCurrentUser() pick up the new avatar URL on the next tick.
    await refreshNuxtData(USER_DATA_KEY)
    return { ok: true }
  } catch (e: any) {
    return { ok: false, error: e?.message || 'Upload failed' }
  }
}
